import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Button, LinearProgress } from "@mui/material";
import { fetchGameOverview } from "../../state/game/game-api-slice";
import { useAppDispatch } from "../../state/hooks";
import { store } from "../../state/store";
import GameOverviewResponse from "../../state/interfaces/GameOverviewResponse";
import t from "../../i18n";

/**
 * 开局前的等待面板。
 *
 * 为什么它存在：Pre-game 阶段棋盘上什么都没有，玩家被扔进地图只会看到一张
 * 空图加一句「Game is waiting to start」。真正想知道的只有三件事——我在等谁、
 * 还差几个人、这局什么时候开。原先这些放在 PHP 的 waiting.php 里，但那是
 * 另一套页面：换个界面进来就断档了。现在等待态直接长在 React 界面里，加入
 * 对局之后不用跳来跳去。
 *
 * 数据全部来自 game/overview（api.php 的 GetGameMembers/getMemberData），
 * 没有额外接口：members 按 member id 升序（objects/members.php:231 的
 * "m.id ASC"），也就是入局顺序，正好用来填席位。
 */

// 面板压在最上层：WDGameProgressOverlay 用的是 Z_INDEX 4，这里要盖住它。
const Z_INDEX = "5";

const scrimStyle: React.CSSProperties = {
  position: "fixed",
  left: 0,
  top: 0,
  height: "100%",
  width: "100%",
  backgroundColor: "rgba(18,18,20,0.72)",
  zIndex: Z_INDEX,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  overflowY: "auto",
};

const cardStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 18px 48px rgba(0,0,0,0.35)",
  width: "100%",
  maxWidth: "560px",
  padding: "28px 28px 22px",
  color: "#1b1b1f",
  fontFamily:
    '"Segoe UI", Roboto, "Helvetica Neue", "PingFang SC", "Microsoft YaHei", sans-serif',
};

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#8a8a93",
  fontWeight: 600,
};

const sectionStyle: React.CSSProperties = {
  marginTop: "22px",
};

const pressLabels: Record<string, string> = {
  NoPress: "无密码局",
  PublicPressOnly: "仅公开密码局",
  RulebookPress: "规则书密码局",
  Regular: "标准密码局",
};

const drawLabels: Record<string, string> = {
  "draw-votes-hidden": "和局投票（不公开）",
  "draw-votes-public": "和局投票（公开）",
};

function formatStartTime(processTime: number | null | undefined): string {
  if (!processTime) return "";
  try {
    return new Date(processTime * 1000).toLocaleString();
  } catch (e) {
    return "";
  }
}

interface WDPreGameWaitingProps {
  overview: GameOverviewResponse;
}

const WDPreGameWaiting: React.FC<WDPreGameWaitingProps> = function ({
  overview,
}) {
  const dispatch = useAppDispatch();
  const [refreshing, setRefreshing] = useState(false);

  const countries = overview.variant?.countries ?? [];
  const seats = countries.length || 7;
  const members = overview.members ?? [];
  const joined = members.length;
  const openSeats = Math.max(0, seats - joined);
  const myUserID = overview.user?.member?.userID;
  const isFull = openSeats === 0;

  const shareLink = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/board.php?gameID=${overview.gameID}`;
  }, [overview.gameID]);

  /*
   * 自己也在 members 里（api.php 的 getData 对自己的成员走了
   * retrievePrivateData），所以用 userID 就能标出「你」；匿名局里
   * 别人显示为「匿名」，自己仍然显示真名。
   *
   * key 在这里算好，而不是在 JSX 里用数组下标 —— 匿名局里所有别人的
   * userID 都被抹成 -1，只有一个稳定业务字段可依；下标做 key 还会被
   * react/no-array-index-key 拦下。
   */
  const seatsData: {
    key: string;
    member: boolean;
    name: string;
    isYou: boolean;
  }[] = [];
  for (let i = 0; i < seats; i += 1) {
    const member = members[i];
    const isYou = !!member && member.userID === myUserID;
    let name = t("Anonymous");
    if (!member) {
      name = "";
    } else if (member.username) {
      name = member.username;
    } else if (isYou) {
      name = t("You");
    }
    let key = `open-${countries[i] ?? i}`;
    if (member) {
      key = member.userID > 0 ? `u${member.userID}` : `anon-${i}`;
    }
    seatsData.push({ key, member: !!member, name, isYou });
  }

  const refresh = () => {
    setRefreshing(true);
    dispatch(fetchGameOverview({ gameID: String(overview.gameID) })).then(
      () => setRefreshing(false),
      () => setRefreshing(false),
    );
  };

  /*
   * 别人加入时后端不会推 pusher 事件（api.php 只在投票和阶段推进时
   * trigger overview），所以这里自己轮询。不然玩家只能干瞪眼等别人加入，
   * 数字却不动。
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if (!store.getState().game.outstandingOverviewRequests) {
        dispatch(fetchGameOverview({ gameID: String(overview.gameID) }));
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [dispatch, overview.gameID]);

  return (
    <div style={scrimStyle}>
      <div style={cardStyle}>
        {/* ---------- 标题 ---------- */}
        <div style={labelStyle}>{t("Waiting for players")}</div>
        <h1
          style={{
            margin: "4px 0 0",
            fontSize: "22px",
            lineHeight: 1.25,
            fontWeight: 700,
          }}
        >
          {overview.name}
        </h1>

        {/* ---------- 进度 ---------- */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "10px",
            marginTop: "18px",
          }}
        >
          <span
            style={{
              fontSize: "34px",
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            {joined}
            <span style={{ color: "#b6b6bd", fontWeight: 500 }}>
              {" "}
              / {seats}
            </span>
          </span>
          <span style={{ fontSize: "14px", color: "#55555e" }}>
            {isFull ? (
              t("Everyone has joined")
            ) : (
              <>
                {t("Waiting for")} <strong>{openSeats}</strong>{" "}
                {openSeats === 1 ? t("more player") : t("more players")}
              </>
            )}
          </span>
        </div>

        <LinearProgress
          variant="determinate"
          value={seats ? (joined / seats) * 100 : 0}
          sx={{
            mt: "10px",
            height: "6px",
            borderRadius: "3px",
            backgroundColor: "#ececf0",
            "& .MuiLinearProgress-bar": { backgroundColor: "#1b1b1f" },
          }}
        />

        {isFull ? (
          <div
            style={{ marginTop: "10px", fontSize: "13px", color: "#55555e" }}
          >
            {t("Starts")} {formatStartTime(overview.processTime)}
          </div>
        ) : null}

        {/* ---------- 席位 ---------- */}
        <div style={sectionStyle}>
          <div style={labelStyle}>{t("Seats")}</div>
          <div
            style={{
              marginTop: "8px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
              gap: "8px",
            }}
          >
            {seatsData.map((seat) => (
              <div
                key={seat.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  height: "42px",
                  padding: "0 10px",
                  borderRadius: "8px",
                  border: seat.member
                    ? "1px solid #e3e3e8"
                    : "1px dashed #c9c9d1",
                  backgroundColor: seat.member ? "#fafafb" : "transparent",
                }}
              >
                <span
                  style={{
                    flex: "0 0 auto",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: seat.member ? "#fff" : "#b6b6bd",
                    backgroundColor: seat.member ? "#1b1b1f" : "transparent",
                    border: seat.member ? "none" : "1px dashed #c9c9d1",
                  }}
                >
                  {seat.member ? seat.name.slice(0, 1).toUpperCase() : "+"}
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: seat.member ? "#1b1b1f" : "#a6a6ae",
                  }}
                >
                  {seat.member ? seat.name : t("Open")}
                  {seat.isYou ? (
                    <span style={{ color: "#8a8a93", marginLeft: "4px" }}>
                      {t("(you)")}
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#8a8a93" }}>
            {t("Countries are assigned when the game starts.")}
          </div>
        </div>

        {/* ---------- 本局设置 ---------- */}
        <div style={sectionStyle}>
          <div style={labelStyle}>{t("Settings for this game")}</div>
          <div style={{ marginTop: "6px" }}>
            {[
              [t("Phase length"), `${overview.phaseMinutes} ${t("minutes")}`],
              [
                t("Bet to join"),
                overview.minimumBet
                  ? `${overview.minimumBet} ${t("points")}`
                  : t("None"),
              ],
              [
                t("Press"),
                pressLabels[overview.pressType] ?? overview.pressType,
              ],
              [
                t("Draw type"),
                drawLabels[overview.drawType] ?? overview.drawType,
              ],
              [t("Anonymous"), overview.anon === "Yes" ? t("Yes") : t("No")],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "7px 0",
                  borderBottom: "1px solid #f0f0f3",
                  fontSize: "13px",
                }}
              >
                <span style={{ color: "#6d6d76" }}>{k}</span>
                <span style={{ fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ---------- 邀请 ---------- */}
        <div style={sectionStyle}>
          <div style={labelStyle}>{t("Bring someone in")}</div>
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <input
              readOnly
              value={shareLink}
              onFocus={(e) => e.currentTarget.select()}
              style={{
                flex: 1,
                minWidth: 0,
                height: "34px",
                padding: "0 10px",
                fontSize: "12px",
                color: "#55555e",
                border: "1px solid #e3e3e8",
                borderRadius: "6px",
                backgroundColor: "#fafafb",
              }}
            />
            <Button
              size="small"
              variant="outlined"
              sx={{
                flex: "0 0 auto",
                borderColor: "#d5d5dc",
                color: "#1b1b1f",
                textTransform: "none",
              }}
              onClick={(e) => {
                const input = e.currentTarget.parentElement?.querySelector(
                  "input",
                ) as HTMLInputElement | null;
                if (input) {
                  input.select();
                  try {
                    document.execCommand("copy");
                  } catch (err) {
                    /* http 下没有 async clipboard，忽略 */
                  }
                }
              }}
            >
              {t("Copy link")}
            </Button>
          </div>
        </div>

        {/* ---------- 下一步 ---------- */}
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            disabled={refreshing}
            onClick={refresh}
            sx={{
              backgroundColor: "#1b1b1f",
              "&:hover": { backgroundColor: "#33333a" },
              textTransform: "none",
            }}
          >
            {t("Refresh")}
          </Button>
          <a
            href="/gamelistings.php?gamelistType=My%20games"
            style={{
              fontSize: "13px",
              color: "#6d6d76",
              textDecoration: "none",
            }}
          >
            {t("My games")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default WDPreGameWaiting;
